import { nextTick, ref, computed, onMounted } from 'vue'
import {
  auditStatusOption,
  showStatusOption,
} from '@/views/repository/resource/config/option'
import {
  searchOptions,
  typeOptions,
  isReplaceableOptions,
  onlyInnerOptions,
} from './index'
import type { optionType } from './index.type'
import { fmtSeconds } from '@com/utils/data-format'
import type { TemplateListSearch } from '@/api/veditor/type'
import { LabelType } from '@ve-pro/api/template/type'
import { parseTime } from '@/filters'
import store from '@/store'
import _ from 'lodash'

export default () => {
  const template_tags = ref<LabelType[]>([])
  const industry_categories = ref<LabelType[]>([])

  const formSearchQuery: TemplateListSearch = {
    search_field: 'name',
    keyword: '',
    order: '-updated_time',
    audit_statuses: '',
    show_statuses: '',
    tag_ids: '',
    only_inner: '',
    industry_ids: '',
    is_replaceable: '',
    page: 1,
    page_size: 15,
  }

  async function getVeditorTemplateOption(): Promise<void> {
    const res = await store.dispatch('config/getVeditorConfig')

    industry_categories.value = (_.get(res, 'industry_categories') || []).map(
      (item: LabelType) => ({
        ...item,
        value: item.id,
        label: item.name,
      }),
    )

    template_tags.value = _.get(res, 'template_tags') || []
  }

  function getLabel(options: optionType[], value: string) {
    const temp = options.find((item) => item.value === value)
    if (temp) {
      return temp.label
    }
    return '-'
  }

  function getTemplateLabel(
    options: LabelType[],
    tageIds: string,
    lastLevelNodes: LabelType[] = [],
  ) {
    for (const option of options) {
      const tage_ids = tageIds && tageIds.split(',').map(Number)

      if (!option.child || option.child.length === 0) {
        // @ts-ignore
        if (tage_ids.includes(option.id)) {
          lastLevelNodes.push(option)
        }
      } else {
        getTemplateLabel(option.child, tageIds, lastLevelNodes)
      }
    }

    return lastLevelNodes
  }

  const formItemConfig = computed(() => {
    return [
      {
        prop: 'search_field',
        type: 'select',
        class: 'offer-selector',
        params: {
          options: searchOptions,
        },
      },

      {
        prop: 'keyword',
        type: 'input',
        class: 'offer-input',
        params: {
          placeholder: '请输入关键词',
          clearable: true,
        },
      },
      {
        label: '模版类型:',
        prop: 'types',
        type: 'select',
        params: {
          clearable: true,
          options: typeOptions,
        },
      },
      {
        label: '支持替换:',
        prop: 'is_replaceable',
        type: 'select',
        params: {
          clearable: true,
          options: isReplaceableOptions,
        },
      },
      {
        label: '审核状态:',
        type: 'select',
        prop: 'audit_statuses',
        params: {
          multiple: true,
          clearable: true,
          'collapse-tags': true,
          options: auditStatusOption,
        },
      },
      {
        label: '上架状态:',
        type: 'select',
        prop: 'show_statuses',
        params: {
          multiple: true,
          clearable: true,
          'collapse-tags': true,
          options: showStatusOption,
        },
      },
      {
        label: '是否公开:',
        type: 'select',
        prop: 'only_inner',
        params: {
          clearable: true,
          options: onlyInnerOptions,
        },
      },
      {
        label: '行业:',
        type: 'select',
        prop: 'industry_ids',
        params: {
          multiple: true,
          clearable: true,
          'collapse-tags': true,
          options: industry_categories.value,
        },
      },

      {
        label: '标签:',
        type: 'cascader',
        prop: 'tag_ids',
        params: {
          clearable: true,
          props: {
            children: 'child',
            emitPath: false,
            multiple: true,
            label: 'name',
            value: 'id',
          },
          'collapse-tags': true,
          'show-all-levels': false,
          options: template_tags.value,
        },
      },
    ]
  })

  const tableColumnConfig = computed(() => {
    return [
      {
        prop: 'select-cell',
        slot: 'select-cell',
      },
      {
        label: '模版ID',
        prop: 'id',
        width: 100,
      },
      {
        label: '模版名称',
        prop: 'name',
        algin: 'center',
        slot: 'name',
      },
      {
        slot: 'action-cell',
      },
      {
        prop: 'cover_url',
        slot: 'cover_url',
      },
      {
        label: '更新时间',
        prop: 'updated_time',
        align: 'center',
        sortable: 'updated_time',
        width: 120,
        params: {
          innerHTML(o: any) {
            const { row } = o
            return row.updated_time
              ? parseTime(row.updated_time, '{y}-{m}-{d} {h}:{i}:{s}')
              : '-'
          },
        },
      },
      {
        label: '时长',
        prop: 'duration',
        align: 'center',
        width: 100,
        params: {
          innerHTML(o: any) {
            const { row } = o
            return row.duration ? fmtSeconds(row.duration / 1000) : '-'
          },
        },
      },
      {
        label: '尺寸',
        prop: 'ratios',
        align: 'center',
      },
      {
        label: '语言',
        prop: 'language',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return row.languages ? '原语言,' + row.languages : '原语言'
          },
        },
      },
      {
        label: '模版类型',
        prop: 'type',
        width: 100,
        params: {
          innerHTML(o: any) {
            const { row } = o
            return getLabel(typeOptions, row.type)
          },
        },
      },
      {
        label: '是否支持快速替换',
        prop: 'is_replaceable',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return getLabel(isReplaceableOptions, row.is_replaceable)
          },
        },
      },
      {
        label: '替换文本数',
        prop: 'replace_text_count',
        align: 'center',
      },
      {
        label: '替换图片数',
        prop: 'replace_image_count',
        align: 'center',
      },
      {
        label: '替换视频数',
        prop: 'replace_video_count',
        align: 'center',
      },
      {
        label: '行业',
        prop: 'industry_ids',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            if (row.industry_ids) {
              const industryArray = industry_categories.value.filter((t) =>
                row.industry_ids.split(',').map(Number).includes(t.id),
              )
              return _.map(industryArray, 'name').join(',')
            }

            return '-'
          },
        },
      },
      {
        label: '标签',
        prop: 'tag_ids',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o

            if (row.tag_ids) {
              const templateTagArray = getTemplateLabel(
                template_tags.value,
                row.tag_ids,
              )
              return _.map(templateTagArray, 'name').join(',')
            }

            return '-'
          },
        },
      },
      {
        label: '视频项目ID',
        prop: 'project_id',
        align: 'center',
      },
      {
        label: '提交审核时间',
        prop: 'audit_time',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return parseTime(row.audit_time, '{y}-{m}-{d} {h}:{i}:{s}')
          },
        },
      },
      {
        label: '审核状态',
        prop: 'audit_status',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return getLabel(auditStatusOption, row.audit_status)
          },
        },
      },
      {
        label: '上架状态',
        prop: 'show_status',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return getLabel(showStatusOption, row.show_status)
          },
        },
      },
      {
        label: '是否公开',
        prop: 'only_inner',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return row.only_inner === 1 ? '否，仅内部使用' : '是，外部可用'
          },
        },
      },
      {
        label: '上架时间',
        prop: 'show_on_time',
        align: 'center',
        params: {
          innerHTML(o: any) {
            const { row } = o
            return parseTime(row.show_on_time, '{y}-{m}-{d} {h}:{i}:{s}')
          },
        },
      },
      {
        label: '使用次数',
        prop: 'use_count',
        align: 'center',
      },
      {
        label: '近30天模版使用数',
        prop: 'recent_used_times',
        align: 'center',
      },
      {
        label: '审核拒绝原因',
        prop: 'remake',
        align: 'center',
      },
      {
        label: '更新人员',
        prop: 'admin_name',
        align: 'center',
      },
    ]
  })

  onMounted(() => {
    getVeditorTemplateOption()
  })

  return {
    template_tags,
    industry_categories,
    formSearchQuery,
    formItemConfig,
    tableColumnConfig,
  }
}
